/** Shared formatting helpers for catalog data display. */

import { logger } from './logger';
import { convertDimension, parseValueWithFraction } from './dimensionConversion';

interface DimensionEntry {
  type: string;
  value: string;
  fraction: string;
  unit?: string;
}

interface DimensionData {
  unit: string;
  dimensions: DimensionEntry[];
}

/** Month names for date formatting */
const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Extract text value from controlled vocabulary objects */
export const extractTextValue = (item: unknown): string => {
  if (typeof item === 'string' && (item.includes("'textValue':") || item.includes("'text_value':"))) {
    try {
      const jsonString = item.replace(/'/g, '"').replace(/False/g, 'false').replace(/True/g, 'true');
      const parsed = JSON.parse(jsonString);
      return parsed.textValue || parsed.text_value || '';
    } catch (error) {
      logger.debug('Failed to parse controlled vocabulary string', error);
      return item;
    }
  }
  if (typeof item === 'object' && item !== null) {
    if ('textValue' in item) {
      return String((item as { textValue: unknown }).textValue);
    }
    return '';
  }
  return typeof item === 'string' ? item : '';
};

/** Join array of vocabulary items into comma-separated string */
export const formatControlledVocabulary = (data: unknown[]): string | null => {
  if (!data || !Array.isArray(data) || data.length === 0) return null;
  return data.map(extractTextValue).filter(Boolean).join(', ');
};

/**
 * Format the descriptions-endpoint creators array into a display string.
 *
 * The descriptions endpoint returns creators as an array of objects with a
 * ``name`` field (e.g. ``[{name: "Jane Doe", isCurrentUser: true}]``), which
 * is different from Getty vocabulary entries (``textValue`` key) used by other
 * fields. Returns null when the array is empty or all names are blank.
 *
 * Also handles the public-API ``creatorsList`` shape which uses the same
 * ``{name}`` structure, as well as plain strings for legacy compatibility.
 */
/**
 * Extract creator display names as an ordered array (owner first, then
 * co-creators) from the descriptions ``creators`` / public ``creatorsList``
 * array shapes (objects with a ``name`` field, or plain strings). Blank names
 * are dropped. Returns ``[]`` when empty. The first element is the catalog
 * owner — only it has an addressable catalog (see GH#1182).
 */
export const getCreatorNames = (creators: unknown): string[] => {
  if (!creators || !Array.isArray(creators) || creators.length === 0) return [];
  return creators.map((c) => {
    if (typeof c === 'string') return c.trim();
    if (typeof c === 'object' && c !== null && 'name' in c) {
      const raw = (c as { name: unknown }).name;
      return typeof raw === 'string' ? raw.trim() : '';
    }
    return '';
  }).filter(Boolean);
};

export const formatCreatorsDisplay = (creators: unknown): string | null => {
  const names = getCreatorNames(creators);
  return names.length > 0 ? names.join(', ') : null;
};

/** Format single controlled vocabulary value */
export const formatSingleControlledVocabulary = (data: unknown): string | null => {
  if (!data) return null;
  if (Array.isArray(data)) {
    return data.length > 0 ? extractTextValue(data[0]) : null;
  }
  return extractTextValue(data);
};

/**
 * Format a raw date string for display, expanding known partial/full ISO
 * shapes into a readable form. Values that don't match a recognized shape
 * (ranges, "ca. " prefixed years, plain years, free text) pass through
 * unchanged.
 *
 * Regression fix for #810: format YYYY-MM dates as MMM YYYY.
 * Regression fix for #1229: format full YYYY-MM-DD dates as MM/DD/YYYY. The
 * date is stored normalized to ISO (the wizard's date picker always saves
 * YYYY-MM-DD/YYYY-MM regardless of locale), so the originally-entered
 * format cannot be recovered — MM/DD/YYYY is used to match the reporter's
 * expectation and the date-picker's own on-screen preview format.
 */
const formatDateValueForDisplay = (value: string): string => {
  const fullDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (fullDateMatch) {
    const [, year, month, day] = fullDateMatch;
    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);
    if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
      return value;
    }
    return `${month}/${day}/${year}`;
  }

  const monthYearMatch = value.match(/^(\d{4})-(\d{2})$/);
  if (monthYearMatch) {
    const year = monthYearMatch[1];
    const month = parseInt(monthYearMatch[2], 10);
    // Validate month is in range 1-12
    if (month < 1 || month > 12) {
      return value;
    }
    return `${MONTH_NAMES_SHORT[month - 1]} ${year}`;
  }

  return value;
};

/** Format creation date with type handling */
export const formatCreationDate = (dateData: unknown): string | null => {
  if (!dateData) return null;
  if (typeof dateData === 'string') {
    return formatDateValueForDisplay(dateData);
  }
  if (typeof dateData === 'object' && dateData !== null) {
    // Regression fix for #955: use only the actual date value, never the "date as displayed" field.
    // The display field is a free-text annotation (e.g. "CC89") that should not be shown as a date.
    const dateObj = dateData as { display?: string; value?: string; type?: string };
    const value = dateObj.value;
    if (!value) return null;
    if (dateObj.type === 'Year' && value.includes('-')) {
      return value.split('-')[0];
    }
    // Regression fix for #988: API stores circa dates as { type: 'Circa', value: '2010' } (bare year).
    // Normalize legacy "c. " to "ca. ", and add "ca. " when prefix is absent.
    if (dateObj.type === 'Circa' && !value.startsWith('ca. ')) {
      const stripped = value.startsWith('c. ') ? value.slice(3) : value;
      return `ca. ${stripped}`;
    }
    return formatDateValueForDisplay(value);
  }
  return null;
};

/**
 * Format creation date for thumbnail/card display.
 * - Circa dates (ca. YYYY) show the "ca." prefix: "ca. 2010"
 * - Date ranges (YYYY - YYYY) show the full range: "2009 - 2010"
 * - Circa ranges (ca. YYYY - YYYY) show both: "ca. 2009 - 2010"
 * - Exact dates (YYYY, YYYY-MM, YYYY-MM-DD) show year only: "1989"
 *
 * Regression fix for #967: dates like "1989-12" were shown as "1989-12" on thumbnails.
 * Regression fix for #988: circa prefix and date ranges were stripped on thumbnails.
 *
 * Accepted input shapes (same as formatCreationDate):
 *   String: "1989", "1989-12", "1989-12-15", "ca. 1850", "1850 - 1900", "ca. 1850 - 1900"
 *   Object: { value: string, type?: string, display?: string }
 *
 * Returns a formatted date string, or null if the year cannot be extracted.
 */
export const formatCreationDateThumbnail = (dateData: unknown): string | null => {
  if (!dateData) return null;

  let rawValue: string;

  if (typeof dateData === 'string') {
    rawValue = dateData.trim();
  } else if (typeof dateData === 'object' && dateData !== null) {
    const dateObj = dateData as { display?: string; value?: string; type?: string };
    if (!dateObj.value) return null;
    // Regression fix for #988: API stores circa dates as { type: 'Circa', value: '2010' } (bare year).
    // Prepend "ca. " prefix to the raw value if the type is Circa and no circa prefix exists.
    // Legacy data may carry "c. "; normalize to "ca. " for display.
    const baseValue = dateObj.value.trim();
    if (dateObj.type === 'Circa') {
      if (baseValue.startsWith('ca. ')) {
        rawValue = baseValue;
      } else if (baseValue.startsWith('c. ')) {
        rawValue = `ca. ${baseValue.slice(3)}`;
      } else {
        rawValue = `ca. ${baseValue}`;
      }
    } else {
      rawValue = baseValue;
    }
  } else {
    return null;
  }

  // Detect circa prefix (accept legacy "c. " too, normalize to "ca. ")
  const isCircaCa = rawValue.startsWith('ca. ');
  const isCircaC = !isCircaCa && rawValue.startsWith('c. ');
  const isCirca = isCircaCa || isCircaC;
  const withoutPrefix = isCircaCa
    ? rawValue.slice(4)
    : isCircaC
      ? rawValue.slice(3)
      : rawValue;

  // Detect date range
  const isRange = withoutPrefix.includes(' - ');

  if (isRange) {
    // For ranges, show the full range with optional circa prefix
    // Each part may be a YYYY, YYYY-MM, or YYYY-MM-DD — extract just YYYY for each
    const parts = withoutPrefix.split(' - ');
    const startMatch = parts[0].trim().match(/^(\d{4})/);
    const endMatch = parts[1] ? parts[1].trim().match(/^(\d{4})/) : null;

    if (!startMatch) return null;
    const startYear = startMatch[1];
    const endYear = endMatch ? endMatch[1] : null;

    const rangeStr = endYear ? `${startYear} - ${endYear}` : startYear;
    return isCirca ? `ca. ${rangeStr}` : rangeStr;
  }

  // Exact date — extract leading 4-digit year from any format: YYYY, YYYY-MM, YYYY-MM-DD
  const yearMatch = withoutPrefix.match(/^(\d{4})/);
  if (yearMatch) {
    const year = yearMatch[1];
    return isCirca ? `ca. ${year}` : year;
  }

  return null;
};

/** Dimension label abbreviations for display */
const DIMENSION_LABELS: Record<string, string> = { height: 'H', width: 'W', depth: 'D' };

/** Format "value unit (H) x value unit (W) x value unit (D)" string */
export const formatHeroDimensions = (data: DimensionData | null): string | null => {
  if (!data?.dimensions || data.dimensions.length === 0) return null;

  const parts = ['height', 'width', 'depth']
    .map(type => {
      const dim = data.dimensions.find(d => d.type.toLowerCase() === type);
      if (!dim?.value) return null;
      const value = dim.fraction ? `${dim.value} ${dim.fraction}` : dim.value;
      const dimUnit = dim.unit || data.unit;
      return `${value} ${dimUnit} (${DIMENSION_LABELS[type]})`;
    })
    .filter(Boolean);

  if (parts.length === 0) return null;
  return parts.join(' x ');
};

/**
 * Map a source unit to its preferred dual-display target. Mirrors the
 * conversionTargetFor map in dimensionConversion but defined locally so
 * formatHeroDimensionsDual stays self-contained.
 */
const DUAL_TARGET_UNIT: Record<string, string> = {
  inches: 'cm',
  feet: 'cm',
  cm: 'inches',
  mm: 'inches',
  meters: 'inches',
};

const formatRoundedConversion = (value: number, unit: string): string => {
  const decimals = unit === 'cm' || unit === 'mm' || unit === 'meters' ? 1 : 2;
  return value.toFixed(decimals).replace(/\.?0+$/, '');
};

/**
 * Format hero dimensions with both imperial and metric values when all
 * present dimensions share a convertible unit. Falls back to the
 * single-unit `formatHeroDimensions` output when units are mixed or no
 * conversion target exists.
 *
 * Examples:
 *   `24 × 36 in (61 × 91.4 cm)`
 *   `12 × 18 × 4 in (30.5 × 45.7 × 10.2 cm)`
 */
export const formatHeroDimensionsDual = (data: DimensionData | null): string | null => {
  if (!data?.dimensions || data.dimensions.length === 0) return null;

  const ordered = ['height', 'width', 'depth']
    .map(type => {
      const dim = data.dimensions.find(d => d.type.toLowerCase() === type);
      if (!dim?.value) return null;
      const dimUnit = dim.unit || data.unit;
      return { type, dim, dimUnit };
    })
    .filter((entry): entry is { type: string; dim: DimensionEntry; dimUnit: string } => Boolean(entry));

  if (ordered.length === 0) return null;

  const firstUnit = ordered[0].dimUnit;
  const targetUnit = DUAL_TARGET_UNIT[firstUnit];
  const allSameUnit = ordered.every(e => e.dimUnit === firstUnit);

  if (!targetUnit || !allSameUnit) return formatHeroDimensions(data);

  const primaryValues = ordered.map(e =>
    e.dim.fraction ? `${e.dim.value} ${e.dim.fraction}` : e.dim.value,
  );
  const convertedValues = ordered.map(e => {
    const numeric = parseValueWithFraction(e.dim.value, e.dim.fraction ?? null);
    if (!Number.isFinite(numeric)) return null;
    const converted = convertDimension(numeric, e.dimUnit, targetUnit);
    return Number.isFinite(converted) ? formatRoundedConversion(converted, targetUnit) : null;
  });

  if (convertedValues.some(v => v === null)) return formatHeroDimensions(data);

  return `${primaryValues.join(' × ')} ${firstUnit} (${convertedValues.join(' × ')} ${targetUnit})`;
};

/** Format techniques + materials string, preferring free-text mediaDescription */
export const formatMedium = (
  techniques: unknown[] | null | undefined,
  materials: unknown[] | null | undefined,
  mediaDescription?: string | null,
): string | null => {
  if (mediaDescription?.trim()) return mediaDescription.trim();

  const techniquesStr = techniques ? formatControlledVocabulary(techniques) : null;
  const materialsStr = materials ? formatControlledVocabulary(materials) : null;

  if (techniquesStr && materialsStr) return `${techniquesStr}; ${materialsStr}`;
  return techniquesStr || materialsStr || null;
};

/** Extract a string value from a field that may be a string, array, object with textValue, or object with first value */
const extractFieldValue = (field: unknown): string => {
  if (!field) return '';
  if (typeof field === 'string') return field;
  if (Array.isArray(field)) return field.filter(Boolean).join(', ');
  if (typeof field === 'object' && field !== null) {
    if ('textValue' in field) return String((field as { textValue: unknown }).textValue);
    const vals = Object.values(field);
    return vals.length > 0 ? String(vals[0]) : '';
  }
  return '';
};

/** Format signatures into RL catalog-style single-line inscriptions.
 *  e.g. "Signed, lower right: J. Doe | Dated, lower left: 2024"
 *  Returns null for empty/missing data. */
export const formatInscriptions = (sigData: unknown): string | null => {
  if (!sigData || !Array.isArray(sigData) || sigData.length === 0) return null;

  const formatted = sigData
    .map((sig: Record<string, unknown>) => {
      // Resolve type — may be string, array of strings, or object
      let typeStr = '';
      if (Array.isArray(sig.type)) {
        typeStr = (sig.type as string[]).filter(Boolean).join(' and ');
      } else {
        typeStr = extractFieldValue(sig.type);
      }

      const locationStr = extractFieldValue(sig.location);
      const displayStr = typeof sig.display === 'string' ? sig.display : '';

      // Skip entries with all empty fields
      if (!typeStr && !locationStr && !displayStr) return null;

      // Build inscription: "Type, location: display"
      const parts: string[] = [];
      if (typeStr) parts.push(typeStr);
      if (locationStr) parts.push(locationStr);

      const prefix = parts.join(', ');

      if (prefix && displayStr) return `${prefix}: ${displayStr}`;
      if (prefix) return prefix;
      return displayStr || null;
    })
    .filter(Boolean);

  return formatted.length > 0 ? formatted.join(' | ') : null;
};

/** Format signature type/location/display */
export const formatSignatures = (sigData: unknown): string | null => {
  if (!sigData) return null;
  if (typeof sigData === 'string') return sigData;
  if (Array.isArray(sigData)) {
    if (sigData.length === 0) return null;
    return sigData.map(sig => {
      const parts: string[] = [];

      if (sig.type) {
        const typeValue = typeof sig.type === 'object' ? Object.values(sig.type)[0] : sig.type;
        if (typeValue) parts.push(`Type: ${typeValue}`);
      }

      if (sig.location) {
        const locationValue = Array.isArray(sig.location)
          ? sig.location.filter(Boolean).join(', ')
          : (typeof sig.location === 'object' ? Object.values(sig.location)[0] : sig.location);
        if (locationValue) parts.push(`Location: ${locationValue}`);
      }

      if (sig.display) {
        parts.push(`Display: ${sig.display}`);
      }

      return parts.length > 0 ? parts.join(', ') : null;
    }).filter(Boolean).join(' | ');
  }
  return null;
};

/** Format catalog number: return ALI catalog number or empty string if unavailable */
export const formatCatalogNumber = (artwork: { aliCatalogNumber?: string; artworkId: string }): string => {
  return artwork.aliCatalogNumber || '';
};

/** Truncate text to maxLength, appending ellipsis if truncated */
export const truncateText = (text: string, maxLength: number = 120): string => {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

/**
 * Strip everything except digits and a single decimal point from a valuation
 * amount string so the stored/submitted value is always a clean numeric string
 * (e.g. "1,250.00" → "1250.00", "$1,500" → "1500").
 *
 * Only the first decimal point is kept; subsequent ones are removed to prevent
 * malformed strings like "1.2.3".
 */
export const stripValuationFormatting = (raw: string): string => {
  // Allow digits and at most one decimal point
  let sawDot = false;
  return raw
    .split('')
    .filter((ch) => {
      if (ch >= '0' && ch <= '9') return true;
      if (ch === '.' && !sawDot) {
        sawDot = true;
        return true;
      }
      return false;
    })
    .join('');
};

/**
 * Format a stored numeric string for display with comma thousands-separators.
 * Handles an optional decimal portion and leaves non-numeric / empty values
 * unchanged (so partially-typed entries like "" or "." are returned as-is).
 *
 * Examples: "1250000" → "1,250,000"  |  "1250.5" → "1,250.5"  |  "" → ""
 */
export const formatValuationDisplay = (value: string): string => {
  if (!value) return value;

  const dotIndex = value.indexOf('.');
  const intPart = dotIndex === -1 ? value : value.slice(0, dotIndex);
  const decPart = dotIndex === -1 ? '' : value.slice(dotIndex); // includes the "."

  // Only format if intPart is purely digits (don't mangle in-progress/partial input)
  if (!/^\d+$/.test(intPart)) return value;

  const formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return formatted + decPart;
};
